// Responses leave out rich text fields the request didn't ask for; these fill them in from a
// fallback (such as the submitted form values) without overriding explicit nulls. Without an
// object to fill in, the fallback stands in for the value; without a fallback object, the value is
// returned as it is. rich-text.js re-exports them to JavaScript callers, which rely on that.

interface DescriptionFields {
  description?: string | null
  description_html?: string | null
}

interface RecipeFields {
  recipe?: string | null
  recipe_html?: string | null
}

interface SubscriptionDisclaimerFields {
  subscription_disclaimer?: string | null
  subscription_disclaimer_html?: string | null
}

/** A plain object; arrays and other values are never filled in or used to fill. */
function isMergeableObject<TValue>(value: TValue): value is NonNullable<TValue> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function fillMissing<TField>(value: TField | undefined, fallbackValue: TField | undefined) {
  return typeof value === 'undefined' ? fallbackValue : value
}

type RichTextFields = DescriptionFields & RecipeFields

export function mergeMissingRichTextFields<TValue extends RichTextFields>(
  value: TValue,
  fallbackValue: RichTextFields | null | undefined
): TValue
export function mergeMissingRichTextFields<
  TValue extends RichTextFields,
  TFallback extends RichTextFields | null | undefined,
>(value: TValue | null | undefined, fallbackValue: TFallback): TValue | TFallback
export function mergeMissingRichTextFields<
  TValue extends RichTextFields,
  TFallback extends RichTextFields | null | undefined,
>(value: TValue | null | undefined, fallbackValue: TFallback): TValue | TFallback {
  if (!isMergeableObject(value)) {
    return fallbackValue
  }

  if (!isMergeableObject(fallbackValue)) {
    return value
  }

  return {
    ...value,
    description: fillMissing(value.description, fallbackValue.description),
    description_html: fillMissing(value.description_html, fallbackValue.description_html),
    recipe: fillMissing(value.recipe, fallbackValue.recipe),
    recipe_html: fillMissing(value.recipe_html, fallbackValue.recipe_html),
  }
}

export function mergeMissingRichTextDescriptionFields<TValue extends DescriptionFields>(
  value: TValue,
  fallbackValue: DescriptionFields | null | undefined
): TValue
export function mergeMissingRichTextDescriptionFields<
  TValue extends DescriptionFields,
  TFallback extends DescriptionFields | null | undefined,
>(value: TValue | null | undefined, fallbackValue: TFallback): TValue | TFallback
export function mergeMissingRichTextDescriptionFields<
  TValue extends DescriptionFields,
  TFallback extends DescriptionFields | null | undefined,
>(value: TValue | null | undefined, fallbackValue: TFallback): TValue | TFallback {
  if (!isMergeableObject(value)) {
    return fallbackValue
  }

  if (!isMergeableObject(fallbackValue)) {
    return value
  }

  return {
    ...value,
    description: fillMissing(value.description, fallbackValue.description),
    description_html: fillMissing(value.description_html, fallbackValue.description_html),
  }
}

type ProductRichTextFields = DescriptionFields & SubscriptionDisclaimerFields

export function mergeMissingProductRichTextFields<TValue extends ProductRichTextFields>(
  value: TValue,
  fallbackValue: ProductRichTextFields | null | undefined
): TValue
export function mergeMissingProductRichTextFields<
  TValue extends ProductRichTextFields,
  TFallback extends ProductRichTextFields | null | undefined,
>(value: TValue | null | undefined, fallbackValue: TFallback): TValue | TFallback
export function mergeMissingProductRichTextFields<
  TValue extends ProductRichTextFields,
  TFallback extends ProductRichTextFields | null | undefined,
>(value: TValue | null | undefined, fallbackValue: TFallback): TValue | TFallback {
  if (!isMergeableObject(value)) {
    return fallbackValue
  }

  if (!isMergeableObject(fallbackValue)) {
    return value
  }

  return {
    ...value,
    description: fillMissing(value.description, fallbackValue.description),
    description_html: fillMissing(value.description_html, fallbackValue.description_html),
    subscription_disclaimer: fillMissing(
      value.subscription_disclaimer,
      fallbackValue.subscription_disclaimer
    ),
    subscription_disclaimer_html: fillMissing(
      value.subscription_disclaimer_html,
      fallbackValue.subscription_disclaimer_html
    ),
  }
}
